import { Link } from "react-router-dom";
import { ArrowRight, Phone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PhotoHero, Reveal, Container, SectionTitle, Img } from "@/components/common/Primitives";
import { useConcierge } from "@/components/concierge/ConciergeContext";
import { useLang } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { usePageMeta } from "@/lib/seo";
import { SIZES } from "@/lib/images";
import { HOTEL } from "@/lib/hotel";

const VILLAGE_POS = { blues: "50% 55%", teater: "50% 40%", fjord: "50% 50%" };

export default function About() {
  const { lang, tx, tr } = useLang();
  const { data } = useContent();
  const { ask } = useConcierge();
  usePageMeta(
    tx("Om oss", "About us"),
    tx("Skånevik Fjordhotel er eit familiedrive hotell ved kaien i Skånevik, ei bygd med om lag 1 000 innbyggjarar inst i Skånevikfjorden. 51 rom, restaurant, Strandkafé og basseng.", "Skånevik Fjordhotel is a family-run hotel on the quay in Skånevik, a village of about 1 000 inhabitants at the head of the Skånevik fjord. 51 rooms, restaurant, beach café and pool.")
  );
  const story = data?.story || {};
  const people = story.people || [];
  const timeline = story.timeline || [];
  const values = story.values?.[lang] || story.values?.nn || [];
  const village = story.village || [];
  const s = data?.settings;

  return (
    <div data-testid="about-page">
      <PhotoHero
        testid="about-hero"
        image="/images/village-aerial.jpg"
        position="50% 55%"
        mobilePosition="50% 60%"
        mobileImage="/images/village-aerial.jpg"
        alt={tx("Skånevik sentrum sett frå lufta, med hotellet ved kaien", "Skånevik village from the air, with the hotel by the quay")}
        kicker={tx("Familiedrive hotell · Skånevik · Sunnhordland", "Family-run hotel · Skånevik · Sunnhordland")}
        title={tx("Hotellet ved kaien", "The hotel by the quay")}
        lead={tr(story.intro)}
        caption={tx("Bygda har om lag 1 000 innbyggjarar – og ein bluesfestival kvar sommar", "The village has about 1 000 inhabitants – and a blues festival every summer")}
      />

      {/* Values */}
      {values.length > 0 && (
        <section className="pt-20 lg:pt-32" data-testid="about-values">
          <Container>
            <div className="grid grid-cols-1 gap-8 border-b border-border pb-14 md:grid-cols-3 md:gap-10 lg:pb-20">
              {values.map(([title, text], i) => (
                <Reveal key={title} delay={i * 0.05} className="border-t border-foreground pt-5">
                  <p className="text-sm font-semibold tabular text-muted-foreground">0{i + 1}</p>
                  <h2 className="mt-2 text-[1.5rem] leading-tight tracking-[-0.02em] text-foreground">{title}</h2>
                  <p className="mt-2.5 text-[0.9375rem] leading-relaxed text-muted-foreground">{text}</p>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* Story + timeline */}
      <section className="py-20 lg:py-28" data-testid="about-story">
        <Container className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <SectionTitle size="lg" title={tx("Frå gjestgiveri til fjordhotell", "From guesthouse to fjord hotel")} />
            <p className="mt-5 max-w-[52ch] text-base leading-relaxed text-muted-foreground sm:text-lg">{tx("Det har vore overnatting ved dampskipskaien i Skånevik sidan 1930-talet. Hotellet slik det står i dag er bygd ut i fleire omgangar – og framleis drive av folk som bur i bygda.", "There has been lodging by the steamship quay in Skånevik since the 1930s. The hotel as it stands today was extended in several stages – and is still run by people who live in the village.")}</p>
            <ol className="mt-10 divide-y divide-border border-y border-border" data-testid="about-timeline">
              {timeline.map((t) => (
                <li key={t.year} className="grid grid-cols-1 gap-1 py-5 sm:grid-cols-[8rem_1fr] sm:gap-6">
                  <p className="text-[1.0625rem] font-semibold text-foreground tabular">{t.year}</p>
                  <p className="text-[0.9375rem] leading-relaxed text-muted-foreground">{lang === "en" ? t.en : t.nn}</p>
                </li>
              ))}
            </ol>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.05}>
            <div className="grid grid-cols-5 gap-3 sm:gap-4">
              <Img src="/images/hotel-aerial-portrait.jpg" alt={tx("Hotellet, hagen og kaien sett frå lufta", "The hotel, the garden and the quay from the air")} className="img-zoom col-span-3 aspect-[4/5]" radius="rounded-[22px]" sizes={SIZES.half} position="50% 45%" />
              <div className="col-span-2 flex flex-col gap-3 pt-10 sm:gap-4 sm:pt-14">
                <Img src="/images/garden-tables.jpg" alt={tx("Hagebord ved hotellet", "Garden tables by the hotel")} className="img-zoom aspect-[3/4]" radius="rounded-[18px]" sizes={SIZES.quarter} position="50% 60%" />
                <Img src="/images/lounge.jpg" alt={tx("Peisestova", "The fireplace lounge")} className="img-zoom aspect-square" radius="rounded-[18px]" sizes={SIZES.quarter} />
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Village */}
      {village.length > 0 && (
        <section className="bg-secondary/70 py-20 lg:py-28" data-testid="about-village">
          <Container>
            <SectionTitle size="lg" title={tx("Bygda", "The village")} sub={tx("Skånevik er ei levande bygd heile året – med festival, teater og ein fjord som skiftar farge frå time til time.", "Skånevik is a living village all year – with a festival, theatre and a fjord that changes colour by the hour.")} testid="about-village-title" />
            <div className="mt-10 grid grid-cols-1 gap-x-7 gap-y-10 md:grid-cols-3">
              {village.map((v, i) => (
                <Reveal key={v.id} delay={i * 0.05}>
                  <article data-testid={`about-village-${v.id}`}>
                    <Img src={v.image} alt={tr(v.title)} className="img-zoom aspect-[4/3]" radius="rounded-[22px]" sizes={SIZES.third} position={VILLAGE_POS[v.id]} />
                    <h3 className="mt-4 text-[1.25rem] leading-tight tracking-[-0.02em] text-foreground">{tr(v.title)}</h3>
                    <p className="mt-2 text-[0.9375rem] leading-relaxed text-muted-foreground">{tr(v.text)}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* Illustrated map */}
      <section className="py-20 lg:py-28" data-testid="about-map-section">
        <Container className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center lg:gap-16">
          <Reveal className="lg:col-span-5 lg:order-2">
            <SectionTitle size="lg" title={tx("Alt ligg ved kaien", "Everything is by the quay")} />
            <p className="mt-5 max-w-[48ch] text-base leading-relaxed text-muted-foreground sm:text-lg">{tx("Hotellet, Strandkaféen, bassenget og småbåthamna ligg på same tomta. Butikk og bakeri er tre minutt å gå, badeøya Peparen fem. Snøggbåten frå Bergen legg til hundre meter frå inngangen.", "The hotel, Strandkaféen, the pool and the marina share the same plot. The shop and bakery are a three-minute walk, Peparen island five. The express boat from Bergen docks a hundred metres from the entrance.")}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild variant="soft" data-testid="about-opplev-link"><Link to="/opplev">{tx("Sjå kva du kan oppleve", "See what to experience")} <ArrowRight className="h-4 w-4" /></Link></Button>
              <Button asChild variant="ghost" data-testid="about-directions-link"><Link to="/praktisk#veg">{tx("Finn vegen hit", "Find your way here")}</Link></Button>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7 lg:order-1" delay={0.05}>
            <figure className="overflow-hidden rounded-[24px] border border-border bg-card p-3 shadow-float sm:p-4" data-testid="about-illustrated-map">
              <Img src="/images/illustrated-village.jpg" alt={tx("Illustrert kart over Skånevik", "Illustrated map of Skånevik")} className="aspect-[3/2]" radius="rounded-[16px]" sizes={SIZES.half} reveal={false} />
              <figcaption className="px-2 pb-1 pt-4 text-sm text-muted-foreground">{tx("Skånevik teikna for oss – med hotellet ved kaien, Peparen og vegen opp mot fjellet.", "Skånevik drawn for us – with the hotel by the quay, Peparen and the road up towards the mountains.")}</figcaption>
            </figure>
          </Reveal>
        </Container>
      </section>

      {/* People (only when content exists) */}
      {people.length > 0 && (
        <section className="bg-secondary/70 py-20 lg:py-28" data-testid="about-people">
          <Container>
            <SectionTitle size="lg" title={tx("Dei du møter", "The people you meet")} />
            <div className="mt-10 grid grid-cols-2 gap-x-7 gap-y-10 lg:grid-cols-4">
              {people.map((p, i) => (
                <Reveal key={p.name} delay={i * 0.05}>
                  <figure data-testid={`about-person-${i}`}>
                    <Img src={p.image} alt={p.name} className="aspect-[4/5]" radius="rounded-[22px]" imgClassName="object-top" sizes={SIZES.quarter} />
                    <figcaption className="mt-4">
                      <p className="text-[1.0625rem] font-semibold text-foreground">{p.name}</p>
                      <p className="mt-0.5 text-sm text-muted-foreground">{tr(p.role)}</p>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* Closing */}
      <section className="pb-20 lg:pb-28" data-testid="about-closing">
        <Container>
          <div className="grain overflow-hidden rounded-[28px] bg-primary px-6 py-10 text-primary-foreground sm:px-10 sm:py-12 lg:flex lg:items-end lg:justify-between lg:gap-12 lg:rounded-[32px] lg:px-14 lg:py-14">
            <div className="max-w-xl">
              <p className="text-sm font-semibold text-white/60">{s?.name || HOTEL.name}</p>
              <h2 className="mt-3 text-[2rem] leading-[1.0] tracking-[-0.035em] text-white sm:text-[2.75rem]">{tx("Velkomen til oss.", "Welcome.")}</h2>
              <p className="mt-4 text-[1.0625rem] leading-relaxed text-white/70">{tx(`Ring oss på ${HOTEL.phoneDisplay} om du lurer på noko – eller spør Fjordverten, som svarar døgnet rundt.`, `Call us on ${HOTEL.phone} if you have any questions – or ask Fjordverten, who answers around the clock.`)}</p>
            </div>
            <div className="mt-7 flex flex-wrap gap-3 lg:mt-0 lg:shrink-0">
              <Button asChild size="lg" variant="inverse" data-testid="about-book-cta"><Link to="/bestill">{tx("Sjå ledige rom", "See available rooms")} <ArrowRight className="h-4 w-4" /></Link></Button>
              <Button size="lg" variant="ghost-light" className="border border-white/20" onClick={() => ask(null)} data-testid="about-ask-cta"><MessageCircle className="h-4 w-4" /> {tx("Spør Fjordverten", "Ask Fjordverten")}</Button>
              <Button asChild size="lg" variant="ghost-light" className="border border-white/20" data-testid="about-call-cta"><a href={HOTEL.tel}><Phone className="h-4 w-4" /> <span className="tabular">{HOTEL.phoneDisplay}</span></a></Button>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
